import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';

import { APIResponse, ResponseDatas } from '../api/types';
import { APP_TYPE, AppType } from '../config';
import { EnvironmentService } from '../environment';
import { StorageService } from '../storage';

type RefreshAccessToken = ResponseDatas<string>;

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private _token?: string;
  private tokenRefreshing?: Promise<string>;

  constructor(
    @Inject(APP_TYPE) private readonly appType: AppType,
    private readonly environmentService: EnvironmentService,
    private readonly http: HttpClient,
    private readonly jwtHelper: JwtHelperService,
    private readonly storageService: StorageService
  ) {}

  get token(): string | undefined {
    if (this._token && this.jwtHelper.isTokenExpired(this._token)) {
      this._token = undefined;
    }

    return this._token;
  }

  async checkAndGetNewToken(): Promise<string> {
    if (this._token && this.jwtHelper.isTokenExpired(this._token)) {
      return this.refreshToken();
    }

    return this.token;
  }

  async clearToken(): Promise<void> {
    this._token = undefined;

    await this.storageService.remove('aaToken');
    await this.storageService.remove('rToken');
    await this.storageService.remove('token');
  }

  async refreshToken(reload?: boolean): Promise<string> {
    const tokenRefreshing = await this.storageService.get('tokenRefreshing');

    if (tokenRefreshing && Number.parseInt(tokenRefreshing) + 10000 > new Date().getTime()) {
      await this.storageService.remove('tokenRefreshing');
    }

    if (this.appType === 'portal' && (await this.storageService.get('tokenRefreshing')) && !this.tokenRefreshing) {
      this.tokenRefreshing = new Promise((resolve, reject) => {
        const hasTokenRefreshed = setInterval(() => {
          this.storageService.get('tokenRefreshing').then((isRefreshing) => {
            if (!isRefreshing) {
              clearInterval(hasTokenRefreshed);
              this.storageService.get('token').then((t) => {
                if (t) {
                  this._token = t;
                  resolve(t);
                } else {
                  reject(new Error('Could not refresh token'));
                }
              });
            }
          });
        }, 100);
      });
    }

    if (this.tokenRefreshing) {
      return this.tokenRefreshing;
    }

    // That's fixing issue when multiple requests are made while token is being refreshed.
    // Remove when token and user services are fully moved to SharedLib.
    this.tokenRefreshing = new Promise(async (resolve, reject) => {
      const token = await this.storageService.get('token');

      if (!reload && token && !this.jwtHelper.isTokenExpired(token)) {
        this._token = token;

        return resolve(token);
      }

      const rToken = await this.storageService.get('rToken');

      if (!rToken) {
        return reject(new Error('User not authenticated'));
      }

      const TimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // App and portal are using different keys for appID
      // @TODO: Fix storage key in the app
      let request: Observable<APIResponse<RefreshAccessToken>>;

      if (this.appType === 'app') {
        const appID = await this.storageService.get('appid');
        const pushToken = await this.storageService.get('pushToken');

        request = this.http.post<APIResponse<RefreshAccessToken>>(
          `${this.environmentService.apiURL}Security/RefreshAccessTokenInternal`,
          { appID, pushToken, rToken, TimeZone }
        );
      } else {
        const appID = await this.storageService.get('appID');
        const user = JSON.parse(await this.storageService.get('user'));
        const UserId = user?.['FK_UserID'];

        request = this.http.post<APIResponse<RefreshAccessToken>>(
          `${this.environmentService.apiURL}Security/RefreshAccessTokenDashboard?n=${Date.now()}`,
          { appID, rToken, TimeZone, UserId }
        );
      }

      request.subscribe({
        next: (res) => {
          const tokenInternal = res?.Result?.datas;

          if (!tokenInternal) {
            this.storageService.remove('rToken');

            return reject(new Error('Could not refresh token'));
          } else if (this.jwtHelper.isTokenExpired(tokenInternal)) {
            return reject(new Error('Token has expired'));
          }

          this.storageService.set('token', tokenInternal);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((res as any).SSORefreshToken) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.storageService.set('rToken', (res as any).SSORefreshToken);
          }

          this._token = tokenInternal;

          return resolve(tokenInternal);
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error refreshing token', err);

          reject(err.message);
        }
      });
    });

    this.tokenRefreshing.finally(() => {
      this.tokenRefreshing = undefined;
    });

    return this.tokenRefreshing;
  }
}
